import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { adobeStandardSearchEvent } from '../../../common/AdobeTagging/adobeStandardSearchEvent';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useGetFipsCode } from '../../../hooks/useGetFipsCode';
import { useGetPlanDefinitions } from '../../../hooks/useGetPlanDefinitions/useGetPlanDefinitions';
import { useGetPostalPoint } from '../../../hooks/useGetPostalPoint/useGetPostalPoint';
import { useLagoon } from '../../../hooks/useLagoon';
import { DateTime } from '../../../utils/DateTime';
import {
  buildPortalData,
  getAvailablePlans,
  getGuestLocationPageName,
  getLobForPortalData,
  getLocationRequirement,
  shouldSkipPlanSelection,
} from '../Guest.utils';
import { getBlueGreenGQLApiUrl } from '../utils';
import { GeospatialModal } from './GeospatialModal';
import {
  ButtonContainer,
  LocationModal,
  LocationModalSection,
  MessagingText,
} from './GuestLocation.style';
import {
  GuestLocation,
  getShowDualCompletePlanMessaging,
  isSinglePlan,
} from './GuestLocation.utils';
import { PostalLocationData } from './GuestLocationInput';
import { GuestLocationSearch } from './GuestLocationSearch';
import {
  LocationSearchBoxWrapper,
  SelectedLocationWrapper,
} from './GuestLocationSelect.style';

type planDetails = {
  medicarePlanType: string;
  planIdentifier: string;
  planName: string;
  searchDirectory: string;
  years: [PlanYear];
  portalSource?: string;
};

type PlanYear = {
  planYear: string;
  reciprocityId: string;
  telehealthIndicator: boolean;
  displayAcceptsMedicaid: boolean;
  clusterKey: string;
  groupDriven: boolean;
  guestPath: string;
};

type GuestLocationSelectProps = {
  selectedLocation?: GuestLocation;
  setSelectedLocation: (a: GuestLocation) => void;
  isDataLoaded: boolean;
  setIsDataLoaded: (a: boolean) => void;
};

export const GuestLocationSelect = ({
  selectedLocation,
  setSelectedLocation,
  isDataLoaded,
  setIsDataLoaded,
}: GuestLocationSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useState<GuestLocation>(
    selectedLocation || ({} as GuestLocation)
  );
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [usPostalPointId, setUsPostalPointId] = useState('');
  const [isGeospatialOpen, setIsGeospatialOpen] = useState(false);
  const [
    requireNavigateAfterLocationSelect,
    setRequireNavigateAfterLocationSelect,
  ] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<
    GuestLocation | undefined
  >(undefined);

  useEffect(() => {
    if (selectedLocation) {
      setLocation(selectedLocation);
    }
  }, [selectedLocation]);
  useEffect(() => {
    if (location?.label) {
      setCurrentInputValue(location.label);
    }
  }, [location?.label]);
  const storedPlans = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
  );
  const [availablePlans, setAvailablePlans] = useState<any[]>(
    storedPlans ? JSON.parse(storedPlans) : []
  );
  const stateCode = location?.stateCode;
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';
  const lob = searchParams?.get('planSelectionLob') || '';
  const chipValue = searchParams?.get('chipValue') || '';

  const isGuestDeepLinkFlow =
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.SOURCE) ===
    'deeplink';

  // Check if deeplink has plan identifier (delsysCode)
  const hasDeeplinkPlanIdentifier = Boolean(
    sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.DELSYS_CODE) &&
      isGuestDeepLinkFlow
  );

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const lagoonMessagingTable = useLagoon('ui-messaging');
  const lagoonUIMessage =
    typeof lagoonMessagingTable === 'function'
      ? lagoonMessagingTable()
      : lagoonMessagingTable;

  const lagoonConfigTable = useLagoon('config');
  const planSelectionConfig =
    typeof lagoonConfigTable === 'function'
      ? lagoonConfigTable()
      : lagoonConfigTable;
  const signInUrl = planSelectionConfig?.find(
    (row: any) => row.key === `SIGNIN_URL_${lob?.toUpperCase()}`
  )?.value;

  const selectLocationMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_MESSAGE,
  })?.message;
  const locationModalHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_HEADING,
  })?.message;
  const locationModalSubheadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SUBHEADING,
  })?.message;
  const locationModalSignInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.message;
  const locationModalSignInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.url;
  const locationModalSearchText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SEARCH,
  })?.message;
  const dualCompletePlanMessaging = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.DUAL_COMPLETE_PLAN_MESSAGING,
  })?.message;
  const selectLocationHeadingText = find(lagoonUIMessage, {
    key: isSinglePlan(lob, coverageType)
      ? ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_SINGLE_PLAN_HEADING
      : ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_HEADING,
  })?.message;

  const showDualCompletePlanMessaging = getShowDualCompletePlanMessaging(
    coverageType,
    lob,
    selectedLocation
  );

  const [postalPointResult, getPostalPoint] = useGetPostalPoint({});

  const currentYear =
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();
  const [, getPlanDetails] = useGetPlanDefinitions({
    url: guestBGApiUrl,
    onCompleted: (result: {
      data: {
        getPlanDefinitions: {
          planCategories: [
            { planCategory: string; planDetails: [planDetails] }
          ];
          planDetails: [planDetails];
        };
      };
    }) => {
      const combinedPlans = getAvailablePlans(result?.data?.getPlanDefinitions);
      setAvailablePlans(combinedPlans || []);
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(combinedPlans || [])
      );
      setIsDataLoaded(true);
    },
  });

  const locationRequirement = getLocationRequirement(lob, coverageType);
  const [, getFipsCode] = useGetFipsCode({});

  const isEI = lob === 'EI';
  const portalSource = 'psx';

  useEffect(() => {
    setIsDataLoaded(false);
    sessionStorage.setItem('countyFipsCode', JSON.stringify(null));
    const planIdentifier = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.DELSYS_CODE
    );
    if (locationRequirement === 'n/a') {
      getPlanDetails({
        variables: {
          planYear: String(currentYear),
          lob,
          coverageType,
          isDualYear: true,
          isEI,
          planIdentifier,
          portalSource,
        },
      });
    } else if (
      locationRequirement === 'county' &&
      location?.latitude &&
      location?.longitude
    ) {
      // Use countyId from postal API if available (for geospatial LOBs)
      if (location?.countyId) {
        const countyFipsCode = location.countyId;
        sessionStorage.setItem('countyFipsCode', countyFipsCode);
        getPlanDetails({
          variables: {
            planYear: String(currentYear),
            lob,
            coverageType,
            stateCode,
            countyFipsCode,
            isDualYear: true,
            isEI,
            planIdentifier,
            portalSource,
          },
        });
      } else {
        // Fall back to getFipsCode for other LOBs
        getFipsCode({
          variables: {
            lat: parseFloat(location.latitude).toFixed(10),
            long: parseFloat(location.longitude).toFixed(10),
          },
        }).then((res: any) => {
          const countyFipsCode = res?.data?.getFipsCode?.fipsCode;
          sessionStorage.setItem('countyFipsCode', countyFipsCode);
          getPlanDetails({
            variables: {
              planYear: String(currentYear),
              lob,
              coverageType,
              stateCode,
              countyFipsCode,
              isDualYear: true,
              isEI,
              planIdentifier,
              portalSource,
            },
          });
        });
      }
    } else if (location?.stateCode) {
      getPlanDetails({
        variables: {
          planYear: String(currentYear),
          lob,
          coverageType,
          stateCode,
          isDualYear: true,
          isEI,
          planIdentifier,
          portalSource,
        },
      });
    }
  }, [
    location?.latitude,
    location?.longitude,
    location?.stateCode,
    location?.countyId,
    lob,
    coverageType,
    currentYear,
  ]);

  // ─── Navigation ──────────────────────────────────────────────────────────────

  /**
   * Single navigation function used by all selection flows (submit button,
   * modal search, geospatial confirmation, and deeplink auto-navigate).
   * Pass `fireAdobeEvent: true` for user-initiated searches.
   */
  const navigateAfterSelection = (
    loc: GuestLocation,
    { fireAdobeEvent = false } = {}
  ) => {
    const plansAvailable: any[] = JSON.parse(
      sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS) ||
        '[]'
    );
    const selectedPlan = plansAvailable[0]?.planDetails?.[0];
    const locStateCode = loc?.stateCode || '';

    if (fireAdobeEvent) {
      adobeStandardSearchEvent({
        isGuest: true,
        numberOfResults: plansAvailable.length,
        method: 'autofill',
        term: loc?.label || '',
        type: 'site',
      });
    }

    if (shouldSkipPlanSelection(plansAvailable)) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
        JSON.stringify({
          selectedPlan,
          lob,
          coverageType: coverageType || 'M',
          stateCode: locStateCode,
        })
      );
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(loc)
      );
      const searchTerm = sessionStorage.getItem(
        Constants.STORAGE_KEYS.SESSION.SEARCH
      );

      if (searchTerm) {
        navigate(
          `/browse/results/?coverageType=${coverageType}&search=${encodeURIComponent(
            searchTerm
          )}&psx-source=deeplink`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              locStateCode,
              undefined,
              signInUrl
            ),
          }
        );
      } else {
        navigate(
          `/browse?coverageType=${coverageType}&chipValue=${chipValue}`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              locStateCode,
              undefined,
              signInUrl
            ),
          }
        );
      }
    } else {
      navigate(
        `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`,
        { state: plansAvailable, replace: true }
      );
    }
  };

  // ─── Location change handlers ─────────────────────────────────────────────────

  /** Resets multicounty state and updates the active location. */
  const handleDirectLocationSet = (loc: GuestLocation) => {
    setShowCountyDropdown(false);
    setSelectedCounty('');
    setPendingLocation(undefined);
    setUsPostalPointId('');
    sessionStorage.removeItem(
      Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
    );
    setLocation(loc);
  };

  /**
   * Called when the user picks a location from the autocomplete.
   * Queries the postal-point API; if the ZIP spans multiple counties the UI
   * branches based on context: modal → inline county dropdown,
   * main page → GeospatialModal.
   */
  const handlePostalLocationSelected = async (
    data: PostalLocationData,
    isModal: boolean
  ) => {
    const { selectedLocation: pickedLocation, variables } = data;

    // Reset county state immediately while the API call is in flight.
    setShowCountyDropdown(false);
    setSelectedCounty('');
    setPendingLocation(undefined);
    setUsPostalPointId('');

    try {
      const postalPointResponse = await getPostalPoint({ variables });
      const postalPointData = postalPointResponse?.data?.getPostalPoint;
      const postalPoint = Array.isArray(postalPointData)
        ? postalPointData?.[0]
        : postalPointData;

      const multicountyFlag = String(
        postalPoint?.multicounty_flag || ''
      ).trim();

      if (multicountyFlag === 'Y' && postalPoint?.us_postal_point_id) {
        // Multi-county ZIP: let the user choose their county.
        setPendingLocation({
          ...pickedLocation,
          stateCode: postalPoint?.state || pickedLocation.stateCode,
          countyId: postalPoint?.county_id || undefined,
        });
        setUsPostalPointId(postalPoint.us_postal_point_id);

        if (isModal) {
          setShowCountyDropdown(true);
        } else {
          setIsGeospatialOpen(true);
        }
        return;
      }

      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
      setLocation({
        ...pickedLocation,
        stateCode: postalPoint?.state || pickedLocation.stateCode,
        countyId: postalPoint?.county_id || undefined,
      });
    } catch {
      setLocation(pickedLocation);
      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED
      );
    }
  };

  /**
   * Shared county-confirmation logic used by both the GeospatialModal (main
   * page) and the inline county dropdown inside the edit-location modal.
   */
  const handleConfirmCountySelection = () => {
    if (!selectedCounty || !pendingLocation) return;

    const [countyStateCode, countyId] = selectedCounty.split('-');
    const confirmedLocation = {
      ...pendingLocation,
      stateCode: countyStateCode,
      countyId,
    };

    setLocation(confirmedLocation);
    setSelectedLocation(confirmedLocation);
    setIsGeospatialOpen(false);
    setShowCountyDropdown(false);
    setRequireNavigateAfterLocationSelect(true);
    sessionStorage.setItem('countyFipsCode', countyId);
    sessionStorage.setItem(
      Constants.STORAGE_KEYS.SESSION.IS_COUNTY_SELECTED,
      'true'
    );
  };

  /**
   * Handles the edit-location modal search button.
   * When the inline county dropdown is visible the user must confirm their
   * county first; otherwise perform the standard location search.
   */
  const handleModalSearch = () => {
    if (showCountyDropdown) {
      handleConfirmCountySelection();
      setIsOpen(false);
      return;
    }
    adobeLinkTrackEvent({
      name: locationModalSearchText,
      location: locationModalAdobeLabel,
      type: 'internal',
      destinationUrl: '',
    });
    if (location) {
      adobeStandardSearchEvent({
        isGuest: true,
        numberOfResults: availablePlans.length,
        method: 'autofill',
        term: location?.label || '',
        type: 'site',
      });
      setSelectedLocation(location);
    }
    setIsOpen(false);
  };

  // ─── Deep-link auto-navigation ────────────────────────────────────────────────
  useEffect(() => {
    const guestLocation = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
    );
    if (
      (hasDeeplinkPlanIdentifier || requireNavigateAfterLocationSelect) &&
      isDataLoaded &&
      availablePlans.length > 0 &&
      guestLocation
    ) {
      setRequireNavigateAfterLocationSelect(false);
      navigateAfterSelection(location);
    }
  }, [
    hasDeeplinkPlanIdentifier,
    isDataLoaded,
    availablePlans,
    location,
    requireNavigateAfterLocationSelect,
  ]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    guestLob: getLobForPortalData(lob),
    isGuest: true,
    pageName: getGuestLocationPageName(coverageType),
    sitesectionLevel1: 'guest plan selection',
    sitesectionLevel2: 'select coverage type',
  });

  useEffect(() => {
    adobePageTrackEvent();
    // Navigate to browse for deeplinks with single plan OR with plan identifier (delsysCode)
    if (
      isGuestDeepLinkFlow &&
      (isSinglePlan(lob, coverageType) || hasDeeplinkPlanIdentifier) &&
      location
    ) {
      navigateAfterSelection(location);
    }
  }, []);

  const locationModalAdobeLabel = 'edit location modal';
  useEffect(() => {
    if (isOpen) {
      adobeModalTrackEvent(locationModalAdobeLabel);
    }
  }, [isOpen]);

  return (
    <React.Fragment>
      <GeospatialModal
        isOpen={isGeospatialOpen}
        lob={lob}
        onClose={() => setIsGeospatialOpen(false)}
        onSearch={handleConfirmCountySelection}
        selectedCounty={selectedCounty}
        setSelectedCounty={setSelectedCounty}
        usPostalPointId={usPostalPointId}
      />

      <LocationModal
        closeOnClickOutside={false}
        isOpen={isOpen}
        onClose={() => {
          adobeLinkTrackEvent({
            name: 'close',
            location: locationModalAdobeLabel,
            type: 'internal',
            destinationUrl: '',
          });
          setIsOpen(false);
        }}
        title={<Heading offset={3}>{locationModalHeadingText}</Heading>}
      >
        <LocationModalSection>
          <Text
            css={{ marginBottom: '8px' }}
            data-auto-testid="modal-location-heading"
            data-testid="modal-location-heading"
          >
            {locationModalSubheadingText}
          </Text>
          <GuestLocationSearch
            currentInputValue={currentInputValue}
            isCountyDropdownLoading={postalPointResult?.loading}
            isDataLoaded={isDataLoaded}
            isLocationModal={true}
            lob={lob}
            location={location}
            onPostalLocationSelected={(data) =>
              handlePostalLocationSelected(data, true)
            }
            selectedCounty={selectedCounty}
            setCurrentInputValue={setCurrentInputValue}
            setLocation={handleDirectLocationSet}
            setSelectedCounty={setSelectedCounty}
            setShowCountyDropdown={setShowCountyDropdown}
            showCountyDropdown={showCountyDropdown}
            usPostalPointId={usPostalPointId}
          />
          <ButtonContainer>
            <Button
              data-auto-testid="modal-search-button"
              data-testid="modal-search-button"
              disabled={
                selectedLocation === undefined ||
                !isDataLoaded ||
                !currentInputValue?.trim() ||
                postalPointResult?.loading ||
                (showCountyDropdown && !selectedCounty?.trim())
              }
              onClick={handleModalSearch}
              size="$md"
              type="button"
              variant="solid"
            >
              {locationModalSearchText}
            </Button>
            <Button
              css={{ marginLeft: '8px' }}
              data-auto-testid="modal-sign-in-button"
              data-testid="modal-sign-in-button"
              href={locationModalSignInUrl}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: locationModalSignInText,
                  location: locationModalAdobeLabel,
                  type: 'internal',
                  destinationUrl: locationModalSignInUrl,
                });
              }}
              openNewWindow={false}
              size="$md"
              variant="outline"
            >
              {locationModalSignInText}
            </Button>
          </ButtonContainer>
        </LocationModalSection>
      </LocationModal>

      <Flex
        alignContent="center"
        css={{ marginBottom: '20px', gap: '8px' }}
        direction="column"
        justify="center"
      >
        <Heading offset={3} textAlign="center">
          {selectLocationHeadingText}
        </Heading>
        <MessagingText size="$md">{selectLocationMessageText}</MessagingText>
        {showDualCompletePlanMessaging && (
          <MessagingText
            data-auto-testid="dual-complete-plan-messaging"
            data-testid="dual-complete-plan-messaging"
            size="$md"
          >
            {dualCompletePlanMessaging}
          </MessagingText>
        )}
      </Flex>

      {selectedLocation ? (
        <SelectedLocationWrapper
          alignItems="center"
          direction="row"
          id="selected-location-container"
          justify="center"
        >
          <IconSymbol color="$gray8" icon="location_on" />
          <Text>{selectedLocation?.label}</Text>
          <Button
            aria-haspopup="dialog"
            data-testid="edit-location-button"
            onClick={() => {
              adobeLinkTrackEvent({
                name: t('PRIMARY_CARE_PROVIDER.EDIT'),
                location: 'Choose a plan',
                type: 'internal',
                destinationUrl: '',
              });
              setIsOpen(true);
            }}
            size={'$sm'}
            variant="tertiary"
          >
            {t('PRIMARY_CARE_PROVIDER.EDIT')}
          </Button>
        </SelectedLocationWrapper>
      ) : (
        <LocationSearchBoxWrapper justify="center">
          <GuestLocationSearch
            currentInputValue={currentInputValue}
            isCountyDropdownLoading={postalPointResult?.loading}
            isDataLoaded={isDataLoaded}
            lob={lob}
            location={location}
            onPostalLocationSelected={(data) =>
              handlePostalLocationSelected(data, false)
            }
            onSubmit={() => {
              if (location) setSelectedLocation(location);
              navigateAfterSelection(location as GuestLocation, {
                fireAdobeEvent: true,
              });
            }}
            selectedCounty={selectedCounty}
            setCurrentInputValue={setCurrentInputValue}
            setLocation={handleDirectLocationSet}
            setSelectedCounty={setSelectedCounty}
            setShowCountyDropdown={setShowCountyDropdown}
            showCountyDropdown={showCountyDropdown}
            usPostalPointId={usPostalPointId}
          />
        </LocationSearchBoxWrapper>
      )}
    </React.Fragment>
  );
};
